"use client";

import { useEffect } from "react";

/**
 * WO-SITE-REAL-002 · M4 — THE 61 CONTROLS THAT HAND THE VISITOR BACK TO THE OLD SITE.
 *
 * FOUNDER, twice in one sitting: *"the urls only switch mountains — the entire site has individual
 * urls, every single page has a unique url, why is this not working."* The top-tab correlation was
 * measured GREEN, so the WO's instruction was to look at the OTHER controls. This is what they were.
 *
 * MEASURED 2026-08-13 on the served build with `control-clickthrough-gate.mjs`, home screen alone:
 * of 197 visible controls, **61 navigate to `https://www.snowbus.com/...`** — FAQ, contact, deals,
 * perks, privacy, terms, /trips, and every "Bus to <mountain>" row. Seventeen of the twenty distinct
 * destinations are paths THIS BUILD SERVES ITSELF. Click one and you are on the old site, on a page
 * the port already has, with the port's session and cart left behind.
 *
 * WHY NOTHING SAW IT. Every existing check asks whether a link is PRESENT and whether it RESOLVES.
 * These are present and they resolve — to production. `verify_parity` counts them as crawlable links
 * (they are), the URL-sync gate never sees them (no in-app state change), and the dead-control audit
 * classifies them WORKS-ROUTE (an absolute external href is a working route by its rules). A leak to
 * the site you are replacing is invisible to every gate that grades links one at a time.
 *
 * WHY THE FIX IS HERE AND NOT IN THE EXPORT FILE. `sg-beta-v2.01.html` is the founder's artifact and
 * the standing rail is that his design is fixed. It also builds some of these URLs by CONCATENATION
 * (`https://www.snowbus.com/mountain-` + slug + `/bus-schedule`, three sites), so a static rewrite
 * would miss exactly the rows that vary per mountain. Rewriting at the DOM, per anchor, catches both
 * shapes and leaves his file byte-identical.
 *
 * WHAT IT WILL NOT DO — and this is the whole reason it takes a served-path list rather than just
 * stripping the origin: a legacy URL whose path this build does NOT serve is LEFT POINTING AT LIVE.
 * Three of them exist today (`/mountain-mont-tremblant/bus-schedule`,
 * `/mountain-blue-mountain/bus-schedule`, `/mountain-vail/bus-schedule` — export slugs with no page
 * here). Rewriting those would trade a working page on the old site for a 404 on the new one, which
 * is the "flag, never invent" rail pointing the other way. They are counted onto the host element as
 * `data-sg-legacy-left` so the gate can report them instead of the fix hiding them.
 *
 * ── FOUNDER WALK 2026-08-14 · IT NOW WORKS WITH OR WITHOUT A FRAME ───────────────────────────────
 *
 * This file was written against an iframe and gave up on `if (!iframe) return`. That was correct
 * when every export page was framed. TWO THINGS WERE WRONG WITH IT BY 2026-08-14, and they are
 * different problems with the same cause — it is mounted by `ExportShell` and by nothing else:
 *   1. the 30 mountain roots stopped using `ExportShell` when they un-framed, so this component was
 *      never MOUNTED on them at all — thirty pages with no legacy-link bridge and no gate looking;
 *   2. once the other 372 un-framed too, mounting it would not have been enough, because the very
 *      first thing it does is look for a frame and leave.
 * Both are fixed together: the surface is resolved rather than assumed, and the roots branch mounts
 * it. Nothing caught either, because the M4 measurement that produced the 61 was taken on `/` —
 * which was still framed on the day it was measured.
 *
 * So the surface is resolved rather than assumed: the design's document is the IFRAME'S document if
 * there is one, and THIS document if the design mounted in-place. Everything downstream — the
 * per-anchor rewrite, the MutationObserver re-sweep as the export swaps screens, and the capture-
 * phase click fallback — is unchanged and now simply runs against whichever it is. Had this not been
 * generalised in the same commit that un-framed the other 372 paths, that commit would have silently
 * reinstated all 61 leaks on every one of them.
 */
export default function ExportLegacyLinkBridge({ served }: { served: string[] }) {
  useEffect(() => {
    const LEGACY = new Set(["www.snowbus.com", "snowbus.com", "dev.snowbus.com"]);
    const paths = new Set(served);
    const iframe = document.querySelector("iframe[src*='sg-beta']") as HTMLIFrameElement | null;
    // The element the `data-sg-legacy-left` count is written onto. The frame when there is one, the
    // in-document design host otherwise — the gate reads the attribute, not the tag name.
    const host: Element | null = iframe ?? document.querySelector("[data-sg-design-surface]");
    if (!host) return;

    let left = 0;
    const localPathFor = (raw: string): string | null => {
      let u: URL;
      try {
        u = new URL(raw, window.location.origin);
      } catch {
        return null;
      }
      if (!LEGACY.has(u.host.toLowerCase())) return null;
      const p = u.pathname.replace(/\/+$/, "") || "/";
      return paths.has(p) ? p + u.search + u.hash : null;
    };

    const sweep = (doc: Document) => {
      left = 0;
      for (const a of Array.from(doc.querySelectorAll<HTMLAnchorElement>("a[href]"))) {
        const raw = a.getAttribute("href");
        if (!raw) continue;
        let host = "";
        try {
          host = new URL(raw, window.location.origin).host.toLowerCase();
        } catch {
          continue;
        }
        if (!LEGACY.has(host)) continue;
        const local = localPathFor(raw);
        if (local) {
          // The href itself is rewritten, not just the click: the status bar, middle-click,
          // open-in-new-tab and "copy link address" all read the attribute, and a click handler that
          // fixes only the left-click leaves three ways to still land on the old site.
          a.setAttribute("data-sg-legacy-href", raw);
          a.setAttribute("href", local);
          // The export's rows open in a new tab by design; keeping that, but now on this origin.
        } else {
          left++;
        }
      }
      host.setAttribute("data-sg-legacy-left", String(left));
    };

    // The export replaces whole screens as its user navigates, so a one-shot pass would fix the first
    // screen and nothing after it. Observe, and re-sweep when the tree changes.
    let observer: MutationObserver | null = null;
    let raf = 0;
    const attach = () => {
      // Un-framed, the design's document IS this document — so this is the surface to sweep and to
      // observe. `iframe?.contentDocument` is undefined-or-null exactly when there is no frame, which
      // is the same condition `host` already resolved on.
      const doc = iframe ? iframe.contentDocument : document;
      if (!doc) return;
      sweep(doc);
      observer?.disconnect();
      observer = new MutationObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => sweep(doc));
      });
      observer.observe(doc.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ["href"] });
    };

    // A click on an anchor the sweep has not reached yet (created in the same frame as the click) is
    // still caught here, in the capture phase, before the browser acts on it.
    const onClick = (ev: Event) => {
      const a = (ev.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      const local = localPathFor(a.getAttribute("href") || "");
      if (!local) return;
      ev.preventDefault();
      window.location.assign(local);
    };

    const clickDoc = () => (iframe ? iframe.contentDocument : document);
    const wire = () => {
      attach();
      // Re-adding the same listener with the same capture flag is a no-op per the DOM spec, so the
      // frame's `load` firing more than once cannot stack handlers.
      clickDoc()?.addEventListener("click", onClick, true);
    };
    // Un-framed there is nothing to wait for — the design mounts into this document, and the
    // MutationObserver below is what catches it appearing. Framed, keep the original two-path wiring.
    if (!iframe || iframe.contentDocument?.readyState === "complete") wire();
    iframe?.addEventListener("load", wire);

    return () => {
      observer?.disconnect();
      cancelAnimationFrame(raf);
      iframe?.removeEventListener("load", wire);
      clickDoc()?.removeEventListener("click", onClick, true);
    };
  }, [served]);

  return null;
}
